import { useState, useEffect } from 'react';
import { api } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  BarChart3, TrendingUp, DollarSign, Bed, UtensilsCrossed, 
  Users, Calendar, ArrowUpRight, ArrowDownRight, Store,
  ShoppingBag, Receipt, Clock, ChefHat
} from 'lucide-react';
import { toast } from 'sonner';

export default function ReportsPage({ user }) {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('today');
  const [selectedRestaurant, setSelectedRestaurant] = useState('all');
  const [restaurants, setRestaurants] = useState([]);
  const [reportData, setReportData] = useState(null);

  useEffect(() => {
    loadRestaurants();
  }, []);

  useEffect(() => {
    loadReportData();
  }, [dateRange, selectedRestaurant]);

  const loadRestaurants = async () => {
    try {
      const res = await api.get('/restaurants');
      setRestaurants(res.data);
    } catch (error) {
      console.error('Failed to load restaurants');
    }
  };

  const loadReportData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        date_range: dateRange,
        ...(selectedRestaurant !== 'all' && { restaurant_id: selectedRestaurant })
      });
      
      const res = await api.get(`/reports/summary?${params}`);
      setReportData(res.data);
    } catch (error) {
      // Use mock data if API not available
      setReportData(generateMockData());
    } finally {
      setLoading(false);
    }
  };

  const generateMockData = () => ({
    overview: {
      total_revenue: 156780,
      revenue_change: 12.5,
      total_orders: 234,
      orders_change: 8.3,
      avg_order_value: 670,
      aov_change: 3.8,
      occupancy_rate: 78,
      occupancy_change: 5.2
    },
    revenue_breakdown: {
      rooms: 98500,
      restaurant: 45280,
      services: 13000
    },
    restaurant_sales: restaurants.map(r => ({
      name: r.name,
      revenue: Math.floor(Math.random() * 50000) + 10000,
      orders: Math.floor(Math.random() * 100) + 20
    })),
    top_items: [
      { name: 'Butter Chicken', quantity: 45, revenue: 15750 },
      { name: 'Paneer Tikka', quantity: 38, revenue: 9500 },
      { name: 'Biryani', quantity: 32, revenue: 12800 },
      { name: 'Dal Makhani', quantity: 28, revenue: 5600 },
      { name: 'Naan', quantity: 85, revenue: 4250 }
    ],
    hourly_orders: Array.from({ length: 24 }, (_, i) => ({
      hour: i,
      orders: Math.floor(Math.random() * 20) + (i >= 12 && i <= 21 ? 10 : 2)
    }))
  });

  const formatCurrency = (amount) => `₹${amount.toLocaleString()}`;

  const getChangeIcon = (change) => {
    return change >= 0 
      ? <ArrowUpRight className="h-4 w-4 text-green-500" />
      : <ArrowDownRight className="h-4 w-4 text-red-500" />;
  };

  const getChangeColor = (change) => change >= 0 ? 'text-green-600' : 'text-red-600';

  if (loading && !reportData) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="reports-loading">
        <div className="text-center">
          <div className="h-12 w-12 rounded-full border-4 border-primary border-t-transparent animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Loading reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Reports & Analytics</h1>
          <p className="text-gray-500">Track your hotel&apos;s performance</p>
        </div>
        <div className="flex gap-3">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[140px]" data-testid="date-range-select">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
            </SelectContent>
          </Select>
          <Select value={selectedRestaurant} onValueChange={setSelectedRestaurant}>
            <SelectTrigger className="w-[180px]" data-testid="restaurant-filter">
              <Store className="h-4 w-4 mr-2" />
              <SelectValue placeholder="All Restaurants" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Restaurants</SelectItem>
              {restaurants.map(r => (
                <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-700">Total Revenue</p>
                <p className="text-2xl font-bold text-green-800 mt-1">
                  {formatCurrency(reportData?.overview?.total_revenue || 0)}
                </p>
                <div className={`flex items-center gap-1 mt-1 text-sm ${getChangeColor(reportData?.overview?.revenue_change || 0)}`}>
                  {getChangeIcon(reportData?.overview?.revenue_change || 0)}
                  <span>{Math.abs(reportData?.overview?.revenue_change || 0)}%</span>
                </div>
              </div>
              <div className="h-12 w-12 rounded-xl bg-green-200 flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-green-700" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-700">Total Orders</p>
                <p className="text-2xl font-bold text-blue-800 mt-1">
                  {reportData?.overview?.total_orders || 0}
                </p>
                <div className={`flex items-center gap-1 mt-1 text-sm ${getChangeColor(reportData?.overview?.orders_change || 0)}`}>
                  {getChangeIcon(reportData?.overview?.orders_change || 0)}
                  <span>{Math.abs(reportData?.overview?.orders_change || 0)}%</span>
                </div>
              </div>
              <div className="h-12 w-12 rounded-xl bg-blue-200 flex items-center justify-center">
                <ShoppingBag className="h-6 w-6 text-blue-700" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-700">Avg Order Value</p>
                <p className="text-2xl font-bold text-purple-800 mt-1">
                  {formatCurrency(reportData?.overview?.avg_order_value || 0)}
                </p>
                <div className={`flex items-center gap-1 mt-1 text-sm ${getChangeColor(reportData?.overview?.aov_change || 0)}`}>
                  {getChangeIcon(reportData?.overview?.aov_change || 0)}
                  <span>{Math.abs(reportData?.overview?.aov_change || 0)}%</span>
                </div>
              </div>
              <div className="h-12 w-12 rounded-xl bg-purple-200 flex items-center justify-center">
                <Receipt className="h-6 w-6 text-purple-700" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-amber-700">Room Occupancy</p>
                <p className="text-2xl font-bold text-amber-800 mt-1">
                  {reportData?.overview?.occupancy_rate || 0}%
                </p>
                <div className={`flex items-center gap-1 mt-1 text-sm ${getChangeColor(reportData?.overview?.occupancy_change || 0)}`}>
                  {getChangeIcon(reportData?.overview?.occupancy_change || 0)}
                  <span>{Math.abs(reportData?.overview?.occupancy_change || 0)}%</span>
                </div>
              </div>
              <div className="h-12 w-12 rounded-xl bg-amber-200 flex items-center justify-center">
                <Bed className="h-6 w-6 text-amber-700" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Reports */}
      <Tabs defaultValue="revenue" className="space-y-4">
        <TabsList>
          <TabsTrigger value="revenue" data-testid="revenue-tab">
            <DollarSign className="h-4 w-4 mr-2" />
            Revenue
          </TabsTrigger>
          <TabsTrigger value="restaurant" data-testid="restaurant-tab">
            <UtensilsCrossed className="h-4 w-4 mr-2" />
            Restaurant
          </TabsTrigger>
          <TabsTrigger value="items" data-testid="items-tab">
            <ChefHat className="h-4 w-4 mr-2" />
            Top Items
          </TabsTrigger>
        </TabsList>

        {/* Revenue Tab */}
        <TabsContent value="revenue" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Revenue Breakdown</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Bed className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium">Room Revenue</p>
                        <p className="text-sm text-gray-500">Accommodation charges</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">{formatCurrency(reportData?.revenue_breakdown?.rooms || 0)}</p>
                      <p className="text-xs text-gray-500">
                        {((reportData?.revenue_breakdown?.rooms / reportData?.overview?.total_revenue) * 100 || 0).toFixed(1)}%
                      </p>
                    </div>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-blue-500 rounded-full"
                      style={{ width: `${(reportData?.revenue_breakdown?.rooms / reportData?.overview?.total_revenue) * 100 || 0}%` }}
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-orange-100 flex items-center justify-center">
                        <UtensilsCrossed className="h-5 w-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="font-medium">Restaurant Revenue</p>
                        <p className="text-sm text-gray-500">F&B sales</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">{formatCurrency(reportData?.revenue_breakdown?.restaurant || 0)}</p>
                      <p className="text-xs text-gray-500">
                        {((reportData?.revenue_breakdown?.restaurant / reportData?.overview?.total_revenue) * 100 || 0).toFixed(1)}%
                      </p>
                    </div>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-orange-500 rounded-full"
                      style={{ width: `${(reportData?.revenue_breakdown?.restaurant / reportData?.overview?.total_revenue) * 100 || 0}%` }}
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                        <Users className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium">Services Revenue</p>
                        <p className="text-sm text-gray-500">Additional services</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">{formatCurrency(reportData?.revenue_breakdown?.services || 0)}</p>
                      <p className="text-xs text-gray-500">
                        {((reportData?.revenue_breakdown?.services / reportData?.overview?.total_revenue) * 100 || 0).toFixed(1)}%
                      </p>
                    </div>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-purple-500 rounded-full"
                      style={{ width: `${(reportData?.revenue_breakdown?.services / reportData?.overview?.total_revenue) * 100 || 0}%` }}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Peak Hours</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-12 gap-1 h-32">
                  {reportData?.hourly_orders?.map((data, i) => {
                    const maxOrders = Math.max(...(reportData?.hourly_orders?.map(d => d.orders) || [1]));
                    const height = (data.orders / maxOrders) * 100;
                    return (
                      <div key={i} className="flex flex-col items-center justify-end h-full">
                        <div 
                          className={`w-full rounded-t ${height > 60 ? 'bg-orange-500' : height > 30 ? 'bg-orange-300' : 'bg-orange-100'}`}
                          style={{ height: `${Math.max(height, 5)}%` }}
                          title={`${data.hour}:00 - ${data.orders} orders`}
                        />
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between text-xs text-gray-400 mt-2">
                  <span>12AM</span>
                  <span>6AM</span>
                  <span>12PM</span>
                  <span>6PM</span>
                  <span>12AM</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Restaurant Tab */}
        <TabsContent value="restaurant" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Restaurant Performance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {(reportData?.restaurant_sales || restaurants.map(r => ({ name: r.name, revenue: 0, orders: 0 }))).map((restaurant, i) => (
                  <div key={i} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 rounded-xl bg-gradient-orange flex items-center justify-center">
                        <Store className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold">{restaurant.name}</p>
                        <p className="text-sm text-gray-500">{restaurant.orders} orders</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-gray-800">{formatCurrency(restaurant.revenue)}</p>
                      <Badge variant="outline" className="mt-1">
                        Avg: {formatCurrency(restaurant.orders > 0 ? Math.round(restaurant.revenue / restaurant.orders) : 0)}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Top Items Tab */}
        <TabsContent value="items" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Top Selling Items</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {reportData?.top_items?.map((item, i) => (
                  <div key={i} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center text-white font-bold ${
                      i === 0 ? 'bg-yellow-500' : i === 1 ? 'bg-gray-400' : i === 2 ? 'bg-amber-600' : 'bg-gray-300'
                    }`}>
                      {i + 1}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{item.name}</p>
                      <p className="text-sm text-gray-500">{item.quantity} sold</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">{formatCurrency(item.revenue)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
